import React, { useMemo, useState } from "react"
import { METRIC_CATALOG, type MetricCatalogEntry } from "~/shared/kpi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

interface MetricCatalogPickerProps {
  /** Keys already added to the group — shown as selected/disabled. */
  selectedKeys: string[]
  /**
   * Report-scoped entries not in the global catalog (the report's calculated
   * metrics). Merged into the picker; never pushed into the shared singleton.
   */
  extraEntries?: MetricCatalogEntry[]
  onAdd: (key: string) => void
}

// Searchable, category-grouped picker over the shared metric catalog (plus any
// report-scoped extra entries). Clicking an unselected metric adds it to the
// group; already-added metrics render as disabled with a check so the catalog
// stays a single source of truth.
const MetricCatalogPicker: React.FC<MetricCatalogPickerProps> = ({
  selectedKeys,
  extraEntries,
  onAdd,
}) => {
  const [search, setSearch] = useState("")
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys])
  const { metricMap } = useMetricDictionary()

  // Per-render merge — never mutate the shared METRIC_CATALOG singleton (that
  // would leak one report's calc metrics into every report). Labels come from
  // the Metric Dictionary (single source of truth) when catalogued; the
  // catalog's own label is the fallback (e.g. calc/custom metrics).
  const entries = useMemo(() => {
    const base = extraEntries?.length
      ? [...extraEntries, ...METRIC_CATALOG]
      : METRIC_CATALOG
    return base.map(entry => {
      const title = metricEntryFor(metricMap, entry.key)?.title
      return title && title !== entry.label ? { ...entry, label: title } : entry
    })
  }, [extraEntries, metricMap])

  const grouped = useMemo(() => {
    const query = search.trim().toLowerCase()
    const byCategory = new Map<string, MetricCatalogEntry[]>()
    for (const entry of entries) {
      if (
        query &&
        !entry.label.toLowerCase().includes(query) &&
        !entry.category.toLowerCase().includes(query)
      ) {
        continue
      }
      const list = byCategory.get(entry.category) ?? []
      list.push(entry)
      byCategory.set(entry.category, list)
    }
    return [...byCategory.entries()]
  }, [search, entries])

  return (
    <div className="flex flex-col h-full">
      <Input
        fullWidth
        placeholder="Search metrics…"
        value={search}
        onChange={event => setSearch(event.target.value)}
        classes={{ input: "text-white" }}
      />
      <div className="mt-3 flex-1 overflow-y-auto space-y-4 pr-1">
        {grouped.length === 0 && (
          <p className="text-system-blue-300 text-sm">No metrics match.</p>
        )}
        {grouped.map(([category, entries]) => (
          <div key={category}>
            <p className="text-system-blue-300 text-xs font-semibold uppercase tracking-wide mb-1">
              {category}
            </p>
            <div className="space-y-1">
              {entries.map(entry => {
                const isSelected = selected.has(entry.key)
                return (
                  <button
                    key={entry.key}
                    type="button"
                    disabled={isSelected}
                    onClick={() => onAdd(entry.key)}
                    className={cn(
                      "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-system-md text-left text-sm transition-colors",
                      isSelected
                        ? "bg-system-blue-800 text-system-blue-400 cursor-default"
                        : "bg-system-blue-800 text-white hover:bg-system-blue-700",
                    )}
                  >
                    <span className="truncate">{entry.label}</span>
                    {isSelected ? (
                      <span className="text-xs text-system-success-400 flex-shrink-0">
                        Added
                      </span>
                    ) : (
                      <span className="text-system-blue-300 flex-shrink-0">
                        +
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MetricCatalogPicker
